import React from 'react';
import PropTypes from 'prop-types';
import { ToastContext, WeezHeader, Dropdown, Message as NacreMessage } from '@weezevent/nacre';
import i18n from 'i18next';
import { Trans, withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Grid, Message, Table, Placeholder, Icon } from 'semantic-ui-react';
import Loading from '../../components/loading';
import SwapHistoryRow from './components/swapHistoryRow';
import AttendeesActionsManager from './manage/actions/actionManager';
import AttendeeModel from '../../models/attendeeModel';
import RateModel from '../../models/rateModel';
import RateGroup from '../../models/rateGroup';
import Inventory from '../../models/inventory';
import Partner from '../../models/partner';
import AttendeeComments from './components/attendeeComments';
import { FilteredActions } from './manage/actions/actions';
import SeatSwapRequestRowHistory from '../../models/seatSwapRequestRowHistory';
import Barcode from 'react-barcode';
import PageError from '../error/pageError';

class Attendee extends React.Component {
    constructor(...args) {
        super(...args);

        let [
            {
                match: {
                    params: { attendeeId }
                }
            }
        ] = args;
        this.state = {
            attendee: null,
            action: null,
            attendeeId,
            display404: false,
            swapHistory: null,
            rate: null,
            group: null,
            inventory: null,
            booking: null,
            partner: null
        };
    }

    componentDidMount() {
        this.fetch();
    }

    /**
     * Update comment for user
     */
    updateComment(comment) {
        this.setState({ comment });
    }

    /**
     * Fetch attendee data
     */
    fetch() {
        let { organization, event, t } = this.props;
        const organizationId = organization.id;

        AttendeeModel.get({
            organizationId,
            eventId: event.id,
            id: this.state.attendeeId
        })
            .then(attendee => {
                this.setState({
                    attendee: attendee
                });

                RateModel.get({
                    organizationId,
                    eventId: event.id,
                    id: attendee.rate
                })
                    .then(rate => {
                        this.setState({
                            rate: rate
                        });

                        if (rate.group) {
                            RateGroup.get({
                                organizationId,
                                eventId: event.id,
                                id: rate.group
                            })
                                .then(group => {
                                    this.setState({
                                        group: group
                                    });
                                })
                                .catch(e => {
                                    this.context.error(t('attendee.info.groupRetrievalError'));
                                });
                        } else {
                            this.setState({
                                group: { name: '-' }
                            });
                        }
                    })
                    .catch(e => {
                        this.context.error(t('attendee.info.rateRetrievalError'));
                    });

                if (attendee.booking) {
                    Inventory.get({
                        organizationId,
                        chartId: event.chart,
                        id: attendee.booking.inventory
                    })
                        .then(inventory => {
                            this.setState({
                                inventory: inventory
                            });
                        })
                        .catch(e => {
                            this.context.error(t('attendee.info.inventoryRetrievalError'));
                        });
                } else {
                    this.setState({
                        inventory: { name: '-' }
                    });
                }

                if (attendee.partner) {
                    Partner.get({
                        organizationId,
                        id: attendee.partner
                    })
                        .then(partner => {
                            partner.link = `/ticket/O${organizationId}/partners/${partner.uuid}/batches`;
                            this.setState({
                                partner: partner
                            });
                        })
                        .catch(e => {
                            this.context.error(t('attendee.info.partnerRetrievalError'));
                        });
                }
            })
            .catch(e => {
                this.context.error(t('attendee.info.attendeeRetrievalError'));
            });

        SeatSwapRequestRowHistory.getAll({
            organizationId,
            eventId: event.id,
            attendeeId: this.state.attendeeId
        })
            .then(list => {
                this.setState({
                    swapHistory: list
                });
            })
            .catch(e => {
                this.context.error(t('ticket.attendee.swapHistoryError'));
            });
    }

    /**
     * Render a single table row
     */
    renderTableRow(prop, value) {
        if (value instanceof Function) {
            value = value();
        }

        return (
            <Table.Row key={prop}>
                <Table.Cell>{prop}</Table.Cell>
                <Table.Cell>{value}</Table.Cell>
            </Table.Row>
        );
    }

    /**
     * Renders two info spaces into a grid
     */
    renderDualGridInfo(info1, info2) {
        return (
            <Grid columns={2} divided>
                <Grid.Row>
                    <Grid.Column>{info1}</Grid.Column>
                    <Grid.Column>{info2}</Grid.Column>
                </Grid.Row>
            </Grid>
        );
    }

    /**
     * Render a table with prop-value cells
     */
    renderTableInfo(info) {
        return (
            <Table celled definition>
                <Table.Body>{Object.entries(info).map(([prop, value]) => this.renderTableRow(prop, value))}</Table.Body>
            </Table>
        );
    }

    /**
     * Redner info for ticketing
     */
    renderGeneralTicketInfo() {
        return this.renderDualGridInfo(this.renderTicketInfo(), this.renderOrderInfo());
    }

    /**
     * Renders info about the attendee's ticket
     */
    renderTicketInfo() {
        const { t } = this.props;
        const { attendee, rate, group, inventory, partner } = this.state;
        let info = {
            [t('attendee.info.ticketNumber')]: attendee.id_weez_ticket,
            [t('attendee.barcode')]: <Barcode height={30} value={attendee.barcode} />,
            [t('attendee.info.ticketType')]: rate ? (
                rate.name
            ) : (
                <Placeholder>
                    <Placeholder.Line length={'long'} />
                </Placeholder>
            ),
            [t('attendee.info.ticketGroup')]: group ? (
                group.name
            ) : (
                <Placeholder>
                    <Placeholder.Line length={'long'} />
                </Placeholder>
            ),
            [t('attendee.info.validated')]: t(`${attendee.valided ? 'attendee.scanned' : 'attendee.not_scanned'}`)
        };

        if (attendee.partner) {
            info = {
                ...info,
                [t('attendee.info.partner')]: partner ? (
                    <div>
                        <span>{partner.name}</span>{' '}
                        <a target={'_blank'} rel="noopener noreferrer" className="form-link-event" href={partner.link}>
                            <Icon className="external alternate" />
                        </a>
                    </div>
                ) : (
                    <Placeholder>
                        <Placeholder.Line length={'long'} />
                    </Placeholder>
                )
            };
        }

        if (attendee.booking) {
            if (attendee.booking.isGA) {
                info = {
                    ...info,
                    [t('attendee.info.ticketInventory')]: inventory ? (
                        inventory.name
                    ) : (
                        <Placeholder>
                            <Placeholder.Line length={'long'} />
                        </Placeholder>
                    ),
                    [t('attendee.info.zone')]: attendee.booking.seat_zone_name,
                    [t('attendee.info.category')]: attendee.booking.rate_category_name
                };
            } else {
                info = {
                    ...info,
                    [t('attendee.info.ticketInventory')]: inventory ? (
                        inventory.name
                    ) : (
                        <Placeholder>
                            <Placeholder.Line length={'long'} />
                        </Placeholder>
                    ),
                    [t('attendee.info.seat')]: `S${attendee.booking.seat_seat}`,
                    [t('attendee.info.row')]: `R${attendee.booking.seat_row}`,
                    [t('attendee.access')]: attendee.booking.seat_access,
                    [t('attendee.info.zone')]: attendee.booking.seat_zone_name,
                    [t('attendee.info.category')]: attendee.booking.rate_category_name
                };
            }
        }

        return (
            <React.Fragment>
                <h3>
                    <Trans i18nKey="attendee.ticket">Ticket</Trans>
                </h3>
                {!info ? (
                    <Message>
                        <Trans i18nKey="attendees.noTicketInfo">No ticket info to display</Trans>
                    </Message>
                ) : (
                    this.renderTableInfo(info)
                )}
            </React.Fragment>
        );
    }

    /**
     * Renders info about the attendee order
     */
    renderOrderInfo() {
        const { t } = this.props;
        let info = null;
        let attendee = this.state.attendee;
        if (attendee.order) {
            info = {
                [t('attendee.order.reference')]: attendee.order.transaction_reference,
                [t('attendee.order.quantity')]: attendee.order.quantity,
                [t('attendee.order.date')]: new Date(attendee.order.sold_date).toLocaleString(),
                [t('attendees.table.origin')]: t(`attendees.table.originLabel.${attendee.order.origin}`),
                [t('attendee.order.price')]: Intl.NumberFormat(i18n.language, { style: 'currency', currency: attendee.order.currency }).format(attendee.paid_price),
                [t('attendee.order.paymentStatus')]: t(`attendee.order.status.${attendee.order.status}`),
                [t('attendee.order.language')]: attendee.order.locale
            };
        }
        return (
            <React.Fragment>
                <h3>
                    <Trans i18nKey="attendee.orderWord">Order</Trans>
                </h3>
                {!info ? (
                    <Message>
                        <Trans i18nKey="attendees.noOrderInfo">No order info to display</Trans>
                    </Message>
                ) : (
                    <Table celled definition>
                        <Table.Body>
                            {/*<Table.Cell>
                                <button>Order follow-up</button>
                            </Table.Cell>*/}

                            {Object.entries(info).map(([prop, value]) => this.renderTableRow(prop, value))}
                        </Table.Body>
                    </Table>
                )}
            </React.Fragment>
        );
    }

    /**
     * Redner attendee + purchaser info
     */
    renderGeneralAttendeeInfo() {
        return this.renderDualGridInfo(this.renderAttendeeInfo(), this.renderPurchaserInfo());
    }

    /**
     * Renders info about the attendee itself
     */
    renderAttendeeInfo() {
        const { t } = this.props;
        let { attendee } = this.state;
        let data = {
            [t('attendee.reference')]: attendee.id,
            [t('attendee.firstName')]: attendee.firstName,
            [t('attendee.lastName')]: attendee.lastName,
            [t('attendee.barcode')]: <Barcode height={30} value={attendee.barcode} />
        };

        let rows = Object.entries(data).map(([key, prop]) => (
            <Table.Row key={`attendee-${key}`}>
                <Table.Cell>{key}</Table.Cell>
                <Table.Cell>{prop}</Table.Cell>
            </Table.Row>
        ));

        return (
            <React.Fragment>
                <h3>
                    <Trans i18nKey="attendee.info.attendee">Attendee Info</Trans>
                </h3>
                <Table definition celled>
                    <Table.Body>{rows}</Table.Body>
                </Table>
            </React.Fragment>
        );
    }

    /**
     * Render info about the ticket purchaser
     */
    renderPurchaserInfo() {
        const { t } = this.props;
        let info = null;
        let order = this.state.attendee.order;
        if (order) {
            info = {
                [t('attendee.firstName')]: order.buyer_first_name,
                [t('attendee.lastName')]: order.buyer_last_name,
                [t('attendee.purchaser.email')]: order.buyer_email
            };
        }

        return (
            <React.Fragment>
                <h3>
                    <Trans i18nKey="attendee.info.purchaser">Purcharser Info</Trans>
                </h3>
                {!info ? (
                    <Message>
                        <Trans i18nKey="attendees.noPurchaserInfo">No purchaser info to display</Trans>
                    </Message>
                ) : (
                    this.renderTableInfo(info)
                )}
            </React.Fragment>
        );
    }

    /**
     * Render billing info if needed
     */
    renderBillingInfo(billing) {
        return (
            <React.Fragment>
                <h3>
                    <Trans i18nKey="attendee.billing">Billing</Trans>
                </h3>
                {!billing ? (
                    <Message>
                        <Trans i18nKey="attendees.noBillingInfo">No billing info to display</Trans>
                    </Message>
                ) : (
                    this.renderTableInfo(billing)
                )}
            </React.Fragment>
        );
    }

    /**
     * Add a comment to user text-area
     */
    renderCommentInfo() {
        return (
            <div>
                <h3>
                    <Trans i18nKey="attendee.comment">Comments</Trans>
                </h3>
                <AttendeeComments attendeeId={this.state.attendee.id} organization={this.props.organization} event={this.props.event} />
            </div>
        );
    }

    /**
     * Renders seat swap history info
     */
    renderSeatSwapHistoryInfo() {
        const swapHistory = this.state.swapHistory || [];
        // Hide the non confirmed requests
        const displayableSwapHistory = swapHistory.filter(row => row.request_confirmed);
        if (!displayableSwapHistory.length) {
            return null;
        }
        return (
            <div>
                <h3>
                    <Trans i18nKey="ticket.attendee.swapHistory">Swap History</Trans>
                </h3>
                {displayableSwapHistory.map(row => (
                    <SwapHistoryRow key={row.id} row={row} event={this.props.event} />
                ))}
            </div>
        );
    }

    /**
     * Renders billing & comments
     */
    renderBottomRow() {
        return this.renderDualGridInfo(this.renderBillingInfo(), this.renderCommentInfo());
    }

    /**
     * Render title and user name
     */
    renderHeaders() {
        const { attendee } = this.state;
        const { organization, event, t } = this.props;
        const actions = FilteredActions({ event, actions: ['delete'] }).map(action => (
            <Dropdown.Item
                key={action.name}
                item={t(action.label)}
                onClick={() => {
                    this.setState({ action: action.name });
                }}
            />
        ));

        return (
            <WeezHeader
                title={
                    <Trans i18nKey="attendee.title" values={{ name: attendee.first_name, lastName: attendee.last_name }}>
                        Attendee: %name% %lastName%
                    </Trans>
                }
                backLink={{
                    as: Link,
                    to: `/ticket/O${organization.id}/E${event.id}/seating/attendees/manage${window.location.search}`
                }}
                dropdown={actions}
            />
        );
    }

    renderActionManager() {
        const { event, organization } = this.props;
        return (
            <AttendeesActionsManager
                action={this.state.action}
                connectData={null}
                event={this.props.event}
                organization={this.props.organization}
                onActionDone={action => {
                    if (action.name === 'delete') {
                        this.props.history.push(`/ticket/O${organization.id}/E${event.id}/seating/attendees/manage${window.location.search}`);
                    }
                }}
                onActionError={() => {}}
                pyvarUrl={window.weezConfig.api.apiUrl}
                selectedAttendees={[this.state.attendee]}
                rates={null}
                zones={null}
            />
        );
    }

    /**
     * Render page
     */
    render() {
        const { display404, attendee, swapHistory } = this.state;

        if (display404) {
            return <PageError errorCode={404} />;
        }

        if (!attendee) {
            return <Loading />;
        }

        return (
            <React.Fragment>
                {this.renderHeaders()}
                <NacreMessage
                    title={<Trans i18nKey="ticket.common.bannerOldBo.title" />}
                    type="warning"
                    links={[
                        {
                            label: <Trans i18nKey="ticket.common.bannerOldBo.cta" />,
                            url: `${window.weezConfig.temporary.menhirUrl}/bo/tableau_bord_inscrits.php?id_evenement=${this.props.event.id}`
                        }
                    ]}
                >
                    <Trans i18nKey="ticket.common.bannerOldBo.content" />
                </NacreMessage>
                {this.renderGeneralTicketInfo()}
                {this.renderGeneralAttendeeInfo()}
                {this.renderBottomRow()}
                {this.renderActionManager()}
                {swapHistory && swapHistory.length ? this.renderDualGridInfo(this.renderSeatSwapHistoryInfo(), null) : null}
            </React.Fragment>
        );
    }
}

Attendee.contextType = ToastContext;

Attendee.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    history: PropTypes.object
};

export default withTranslation()(Attendee);
